import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  ArrowUpRight,
  BadgeCheck,
  ClipboardList,
  Clock,
  HardHat,
  RefreshCw,
  Search,
  Send,
  UserRoundCog,
  Eye,
  History,
  Copy,
  Check,
  ExternalLink,
  FileText,
  X,
} from "lucide-react";
import LoadingSpinner from "../../common/LoadingSpinner";
import {
  inspectionsAPI,
  projectsAPI,
  supervisorAPI,
} from "../../../services/api";
import { useProjectContext } from "../../../hooks/useProjectContext";
import EscalationInboxPanel from "./EscalationInboxPanel";

const REVIEW_STATUSES = [
  { value: "approved", label: "Approve / Close" },
  { value: "needs_further_action", label: "Needs Further Action" },
];
const RESOLVING_REVIEW_STATUSES = new Set([
  "approved",
  "reviewed",
  "ready_for_closeout",
]);

const FOREMAN_ACTION_OPTIONS = [
  "Resolved - With Action",
  "Resolved - Without Action",
  "Pending",
];

const norm = (value) => (value || "").toString().toLowerCase();
const isSystemAdminRole = (role) => norm(role) === "admin";
const CLOSED_FINDING_STATUSES = new Set(["closed", "completed", "approved"]);
const isClosedFindingStatus = (status) =>
  CLOSED_FINDING_STATUSES.has(norm(status));

const hasCompletedFieldResponse = (finding) => {
  const action = norm(finding?.foreman_action);
  const status = norm(finding?.status);
  if (action === "pending" || status === "pending") return false;

  // Reopen starts a new workflow cycle. Historical response fields may remain for
  // audit/reporting, but must not make the reopened issue look responded.
  if (finding?.latest_reopen_at) {
    const reopenedAt = new Date(finding.latest_reopen_at).getTime();
    const respondedAt = finding?.responded_at
      ? new Date(finding.responded_at).getTime()
      : Number.NaN;
    if (Number.isFinite(reopenedAt) && (!Number.isFinite(respondedAt) || respondedAt <= reopenedAt)) {
      return false;
    }
  }

  return Boolean(
    action ||
      finding?.responded_at ||
      status === "responded" ||
      status === "answered",
  );
};

const hasCompletedAssigneeResponse = (finding) => {
  if (!hasCompletedFieldResponse(finding)) return false;
  const responderId = finding?.responded_by;
  const responsibleId = finding?.responsible_user_id;
  // Keep legacy responses measurable when the responder id was not persisted.
  if (responderId == null || responsibleId == null) return true;
  return Number(responderId) === Number(responsibleId);
};

const isOpenIssue = (finding) =>
  ["open", "pending", "reopened"].includes(norm(finding?.status)) &&
  !hasCompletedFieldResponse(finding);

const ISSUE_METRIC_LEGEND = [
  { label: "Total", description: "All issues in the current reporting scope", dot: "bg-slate-500" },
  { label: "Active", description: "Everything not closed", dot: "bg-orange-500" },
  { label: "Open", description: "Awaiting a final field response", dot: "bg-amber-500" },
  { label: "Need Review", description: "Response awaiting authorized review", dot: "bg-blue-500" },
  { label: "Closed", description: "Review/resolution completed", dot: "bg-emerald-500" },
  { label: "Overdue", description: "Open issue past due", dot: "bg-red-500" },
  { label: "Responded", description: "Assignee response submitted; may still require review", dot: "bg-violet-500" },
];

const needsReview = (finding) => {
  // Backend is the canonical workflow-state source. Keep the legacy calculation only
  // as a fallback while old API responses are still possible during deployment.
  if (typeof finding?.need_review === "boolean") return finding.need_review;
  return (
    hasCompletedFieldResponse(finding) &&
    !RESOLVING_REVIEW_STATUSES.has(norm(finding?.review_status)) &&
    !isClosedFindingStatus(finding?.status)
  );
};
const FIELD_RESPONSIBLE_ROLES = new Set([
  "foreman",
  "general_foreman",
  "supervisor",
]);
const titleCase = (value) =>
  (value || "-")
    .toString()
    .replaceAll("_", " ")
    .split(" ")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const isPastDate = (value) => {
  if (!value) return false;
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return d < today;
};

const statusBadge = (status) => {
  const s = norm(status);
  if (s === "closed") return "bg-gray-100 text-gray-700 border-gray-200";
  if (s === "responded" || s === "answered")
    return "bg-blue-50 text-blue-700 border-blue-100";
  if (s === "open" || s === "pending")
    return "bg-amber-50 text-amber-700 border-amber-100";
  return "bg-slate-50 text-slate-700 border-slate-100";
};

const reviewDecisionLabel = (status) => {
  const s = norm(status);
  if (["approved", "reviewed", "ready_for_closeout"].includes(s)) {
    return "Approved / Closed";
  }
  if (s === "needs_further_action") return "Needs Further Action";
  return status ? titleCase(status) : "Not reviewed";
};

const reviewBadge = (status) => {
  const s = norm(status);
  if (["approved", "reviewed", "ready_for_closeout"].includes(s))
    return "bg-emerald-50 text-emerald-700 border-emerald-100";
  if (s === "needs_further_action")
    return "bg-orange-50 text-orange-700 border-orange-100";
  return "bg-gray-50 text-gray-600 border-gray-200";
};

const KpiCard = ({
  label,
  value,
  hint,
  icon: Icon,
  onClick,
  active = false,
}) => {
  const Component = onClick ? "button" : "div";
  return (
    <Component
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={`w-full rounded-2xl border bg-white p-4 text-left shadow-sm transition ${
        active
          ? "border-orange-400 ring-2 ring-orange-100"
          : "border-gray-200"
      } ${
        onClick
          ? "cursor-pointer hover:-translate-y-0.5 hover:border-orange-300 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-orange-400"
          : ""
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            {label}
          </p>
          <p className="mt-2 text-3xl font-bold text-gray-900">{value ?? 0}</p>
          <p className="mt-1 text-xs text-gray-500">{hint}</p>
          {onClick ? (
            <p className="mt-2 text-xs font-semibold text-orange-700">
              {active ? "Filter active" : "Filter issues →"}
            </p>
          ) : null}
        </div>
        {Icon && (
          <div className="rounded-2xl bg-orange-50 p-3 text-orange-600">
            <Icon className="h-5 w-5" />
          </div>
        )}
      </div>
    </Component>
  );
};

function SearchableForemanSelect({ options, value, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const selected = useMemo(
    () => (options || []).find((m) => Number(m.user_id) === Number(value)),
    [options, value],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options || [];
    return (options || []).filter((m) =>
      [m.name, m.email].filter(Boolean).join(" ").toLowerCase().includes(q),
    );
  }, [options, query]);

  return (
    <div className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-left text-sm shadow-sm hover:border-gray-400 disabled:opacity-60"
      >
        {selected ? (
          <span>
            <span className="font-medium text-gray-900">{selected.name}</span>
            {selected.email ? (
              <span className="text-gray-500"> • {selected.email}</span>
            ) : null}
          </span>
        ) : (
          <span className="text-gray-400">Search/select foreman, GF, or supervisor</span>
        )}
      </button>

      {open && (
        <div className="absolute z-40 mt-2 w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg">
          <div className="flex items-center gap-2 border-b px-3 py-2">
            <Search className="h-4 w-4 text-gray-400" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search responsible team members..."
              className="w-full text-sm outline-none"
            />
          </div>
          <div className="max-h-56 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-3 text-sm text-gray-500">
                No matching team members found.
              </div>
            ) : (
              filtered.map((m) => (
                <button
                  key={m.user_id}
                  type="button"
                  onClick={() => {
                    onChange(Number(m.user_id));
                    setOpen(false);
                  }}
                  className="block w-full px-3 py-2 text-left text-sm hover:bg-orange-50"
                >
                  <span className="block font-medium text-gray-900">
                    {m.name}
                  </span>
                  <span className="block text-xs text-gray-500">
                    {m.email || "No email"}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ReassignModal({ open, finding, foremen, onClose, onSubmit, loading }) {
  const [responsibleUserId, setResponsibleUserId] = useState("");
  const [note, setNote] = useState("");

  useEffect(() => {
    if (!finding) return;
    setResponsibleUserId(finding.responsible_user_id || "");
    setNote("");
  }, [finding]);

  if (!open || !finding) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl">
        <div className="flex items-start justify-between border-b p-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Reassign issue
            </h3>
            <p className="text-sm text-gray-500">
              {finding.finding_number || `Issue #${finding.id}`}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-2 hover:bg-gray-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="space-y-4 p-4">
          <SearchableForemanSelect
            options={foremen}
            value={responsibleUserId}
            onChange={setResponsibleUserId}
            disabled={loading}
          />
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-gray-700">
              Optional note
            </span>
            <textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder="Why is this being reassigned?"
            />
          </label>
        </div>
        <div className="flex justify-end gap-2 border-t p-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border px-4 py-2 text-sm hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={loading || !responsibleUserId}
            onClick={() =>
              onSubmit({
                responsible_user_id: Number(responsibleUserId),
                note: note.trim() || null,
              })
            }
            className="inline-flex items-center gap-2 rounded-lg bg-orange-500 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-600 disabled:opacity-60"
          >
            {loading ? (
              <LoadingSpinner size="small" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            Reassign
          </button>
        </div>
      </div>
    </div>
  );
}

function EscalateFindingModal({ open, finding, targets, targetsLoading, loading, onClose, onSubmit }) {
  const [targetUserId, setTargetUserId] = useState("");
  const [reason, setReason] = useState("");

  useEffect(() => {
    setTargetUserId("");
    setReason("");
  }, [finding]);

  if (!open || !finding) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl">
        <div className="flex items-start justify-between border-b p-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Escalate issue</h3>
            <p className="text-sm text-gray-500">{finding.finding_number || `Issue #${finding.id}`}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-md p-2 hover:bg-gray-100"><X className="h-5 w-5" /></button>
        </div>
        <div className="space-y-4 p-4">
          {targetsLoading ? (
            <div className="flex items-center gap-2 text-sm text-gray-500"><LoadingSpinner size="small" /> Loading reporting-path targets...</div>
          ) : targets.length === 0 ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              No valid escalation target is available in the published organization chart.
            </div>
          ) : (
            <>
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-gray-700">Escalate To *</span>
                <select value={targetUserId} onChange={(e) => setTargetUserId(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500">
                  <option value="">Select reporting-path target</option>
                  {targets.map((target) => <option key={target.user_id} value={target.user_id}>{target.name} — {titleCase(target.role)}</option>)}
                </select>
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-gray-700">Reason *</span>
                <textarea rows={4} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Describe why this issue needs escalation or technical guidance..." className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
              </label>
            </>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t p-4">
          <button type="button" onClick={onClose} className="rounded-lg border px-4 py-2 text-sm hover:bg-gray-50">Cancel</button>
          <button type="button" disabled={loading || targetsLoading || !targetUserId || !reason.trim()} onClick={() => onSubmit({ escalated_to_user_id: Number(targetUserId), reason: reason.trim() })} className="inline-flex items-center gap-2 rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-black disabled:opacity-60">
            {loading ? <LoadingSpinner size="small" /> : <ArrowUpRight className="h-4 w-4" />} Escalate
          </button>
        </div>
      </div>
    </div>
  );
}

function ReviewModal({ open, finding, onClose, onSubmit, loading }) {
  const [status, setStatus] = useState("approved");
  const [note, setNote] = useState("");

  useEffect(() => {
    if (!finding) return;
    const rawCurrent = norm(finding.review_status || finding.gf_review_status);
    const current = ["reviewed", "ready_for_closeout"].includes(rawCurrent)
      ? "approved"
      : rawCurrent || "approved";
    const isCurrentAllowed = REVIEW_STATUSES.some((option) => option.value === current);
    setStatus(isCurrentAllowed ? current : "approved");
    setNote(finding.review_comments || finding.gf_note || "");
  }, [finding]);

  if (!open || !finding) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl">
        <div className="flex items-start justify-between border-b p-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Issue review
            </h3>
            <p className="text-sm text-gray-500">
              {finding.finding_number || `Issue #${finding.id}`}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-2 hover:bg-gray-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="space-y-4 p-4">
          <div className="rounded-lg bg-gray-50 p-3 text-sm text-gray-700">
            <div className="font-medium text-gray-900">Assigned person response</div>
            <div className="mt-1">
              {finding.foreman_action || "No response submitted yet."}
            </div>
            {finding.response_notes ? (
              <div className="mt-1 text-gray-500">{finding.response_notes}</div>
            ) : null}
          </div>
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-gray-700">
              Review decision *
            </span>
            <select
              required
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              {REVIEW_STATUSES.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-gray-700">
              Review comments
            </span>
            <textarea
              rows={4}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder="Add a field leadership note..."
            />
          </label>
        </div>
        <div className="flex justify-end gap-2 border-t p-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border px-4 py-2 text-sm hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={loading || !status}
            onClick={() =>
              onSubmit({
                decision: status,
                comments: note.trim() || null,
              })
            }
            className="inline-flex items-center gap-2 rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-black disabled:opacity-60"
          >
            {loading ? (
              <LoadingSpinner size="small" />
            ) : (
              <BadgeCheck className="h-4 w-4" />
            )}
            {status === "approved" ? "Approve & Close" : "Save review"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ResponsibleResponseModal({ open, finding, actorRole, onClose, onSubmit, loading }) {
  const [foremanAction, setForemanAction] = useState("");
  const [responseNotes, setResponseNotes] = useState("");

  useEffect(() => {
    setForemanAction(finding?.foreman_action || "");
    setResponseNotes(norm(finding?.status) === "reopened" ? "" : finding?.response_notes || "");
  }, [finding]);

  if (!open || !finding) return null;
  const isSupervisorAction =
    norm(actorRole) === "supervisor" && Boolean(Number(finding.responsible_user_id));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl">
        <div className="flex items-start justify-between border-b p-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{isSupervisorAction ? "Add Supervisor Resolution Action" : "Answer for lane Foreman"}</h3>
            <p className="text-sm text-gray-500">{finding.finding_number || `Issue #${finding.id}`}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-md p-2 hover:bg-gray-100"><X className="h-5 w-5" /></button>
        </div>
        <div className="space-y-4 p-4">
          <div className="rounded-lg border bg-gray-50 p-3 text-sm text-gray-700">
            <div className="font-medium text-gray-900">Responsible Person</div>
            <div className="mt-1">{finding.responsible_user_name || "—"}</div>
          </div>
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-gray-700">Response action *</span>
            <select value={foremanAction} onChange={(e) => setForemanAction(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2">
              <option value="">Select action</option>
              {FOREMAN_ACTION_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
            </select>
          </label>
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-gray-700">Response notes</span>
            <textarea rows={5} value={responseNotes} onChange={(e) => setResponseNotes(e.target.value)} className="w-full rounded-lg border border-gray-300 p-3" placeholder={isSupervisorAction ? "Provide your resolution action or field direction..." : "Provide the field response for your lane Foreman..."} />
          </label>
        </div>
        <div className="flex justify-end gap-2 border-t p-4">
          <button type="button" onClick={onClose} className="rounded-lg border px-4 py-2 text-sm hover:bg-gray-50">Cancel</button>
          <button type="button" disabled={loading || !foremanAction} onClick={() => onSubmit({ foreman_action: foremanAction, response_notes: responseNotes.trim() || null })} className="inline-flex items-center gap-2 rounded-lg bg-orange-500 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-600 disabled:opacity-60">
            {loading ? <LoadingSpinner size="small" /> : <Send className="h-4 w-4" />} Submit response
          </button>
        </div>
      </div>
    </div>
  );
}

function FindingScenarioModal({
  open,
  finding,
  history = [],
  historyLoading = false,
  onClose,
  onReview,
  onReassign,
  onOpenFile,
  canReview = false,
  canReassign = false,
}) {
  if (!open || !finding) return null;

  const Field = ({ label, children }) => (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
        {label}
      </div>
      <div className="mt-1 text-sm text-gray-900">{children || "-"}</div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[90vh] w-full max-w-4xl flex-col rounded-2xl bg-white shadow-xl">
        <div className="flex items-start justify-between border-b p-4">
          <div>
            <div className="text-xs uppercase tracking-wide text-gray-500">
              Field issue scenario
            </div>
            <h3 className="mt-1 text-lg font-semibold text-gray-900">
              {finding.finding_number || `Issue #${finding.id}`}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Inspection ID: {finding.inspection_number || finding.inspection_id || "-"} •{" "}
              {finding.inspection_type || "Inspection type not set"}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-2 hover:bg-gray-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 overflow-y-auto p-4">
          <div className="grid grid-cols-1 gap-3 rounded-xl border bg-gray-50 p-3 md:grid-cols-4">
            <Field label="Inspection ID">{finding.inspection_number || finding.inspection_id}</Field>
            <Field label="Inspection Type">{finding.inspection_type}</Field>
            <Field label="Inspection Date">{finding.inspection_date}</Field>
            <Field label="Location">{finding.location}</Field>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="rounded-xl border p-4">
              <h4 className="mb-3 font-semibold text-gray-900">
                Issue details
              </h4>
              <div className="space-y-3">
                <Field label="Description">{finding.description}</Field>
                <Field label="Requested Corrective Action">
                  {finding.corrective_action}
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Severity">{titleCase(finding.severity)}</Field>
                  <Field label="Due Date">{finding.due_date}</Field>
                  <Field label="Issue Status">
                    {titleCase(finding.status)}
                  </Field>
                  <Field label="Created At">{finding.created_at}</Field>
                </div>
              </div>
            </div>

            <div className="rounded-xl border p-4">
              <h4 className="mb-3 font-semibold text-gray-900">
                Field assignment and response
              </h4>
              <div className="space-y-3">
                <Field label="Assigned responsible user">
                  {finding.responsible_user_name || "-"}
                  {finding.responsible_user_email
                    ? ` • ${finding.responsible_user_email}`
                    : ""}
                </Field>
                <Field label="Response Action">
                  {finding.foreman_action || "No response submitted yet."}
                </Field>
                <Field label="Response Notes">
                  {finding.response_notes || finding.response_text}
                </Field>
                <Field label="Responded At">{finding.responded_at}</Field>
              </div>
            </div>
          </div>

          <div className="rounded-xl border p-4">
            <div className="mb-3 flex items-center justify-between gap-3">
              <h4 className="flex items-center gap-2 font-semibold text-gray-900">
                <FileText className="h-4 w-4 text-orange-500" /> Related Files
              </h4>
              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                {finding.files?.length || 0}
              </span>
            </div>
            {finding.files?.length ? (
              <div className="max-h-48 space-y-1.5 overflow-y-auto">
                {finding.files.map((file) => (
                  <button
                    key={file.id}
                    type="button"
                    onClick={() => onOpenFile?.(finding, file)}
                    className="flex w-full items-center justify-between gap-3 rounded-lg border bg-gray-50 px-3 py-2 text-left text-sm hover:border-orange-200 hover:bg-orange-50"
                  >
                    <span className="truncate">{file.name || `File #${file.id}`}</span>
                    <ExternalLink className="h-3.5 w-3.5 shrink-0 text-orange-700" />
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No files are attached to this inspection.</p>
            )}
          </div>

          <div className="rounded-xl border p-4">
            <h4 className="mb-3 flex items-center gap-2 font-semibold text-gray-900">
              <History className="h-4 w-4 text-orange-500" /> Issue history
            </h4>
            {historyLoading ? (
              <div className="text-sm text-gray-500">Loading history…</div>
            ) : history.length === 0 ? (
              <div className="text-sm text-gray-500">
                No history is available.
              </div>
            ) : (
              <div className="max-h-64 space-y-2 overflow-y-auto">
                {history.map((item) => (
                  <div
                    key={item.id}
                    className="rounded-lg border bg-gray-50 p-3 text-sm"
                  >
                    <div className="flex flex-wrap justify-between gap-2 text-xs text-gray-500">
                      <span className="font-semibold text-gray-800">
                        {item.user_name || "System"}{" "}
                        {item.user_role ? `• ${titleCase(item.user_role)}` : ""}
                      </span>
                      <span>
                        {item.created_at
                          ? new Date(item.created_at).toLocaleString()
                          : ""}
                      </span>
                    </div>
                    <div className="mt-1 whitespace-pre-wrap text-gray-800">
                      {item.body}
                    </div>
                    {item.from_status || item.to_status ? (
                      <div className="mt-1 text-xs text-gray-500">
                        {titleCase(item.from_status)} →{" "}
                        {titleCase(item.to_status)}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-xl border p-4">
            <h4 className="mb-3 font-semibold text-gray-900">
              Review history
            </h4>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
              <Field label="Review Status">
                {reviewDecisionLabel(finding.review_status || finding.gf_review_status)}
              </Field>
              <Field label="Reviewed By">
                {finding.reviewed_by_name || finding.gf_reviewed_by_name}
              </Field>
              <Field label="Reviewer Role">
                {titleCase(finding.reviewer_role || "-")}
              </Field>
              <Field label="Reviewed At">
                {finding.reviewed_at || finding.gf_reviewed_at}
              </Field>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap justify-end gap-2 border-t p-4">
          {canReassign ? (
            <button
              type="button"
              onClick={() => onReassign?.(finding)}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
            >
              Reassign
            </button>
          ) : null}
          {canReview ? (
            <button
              type="button"
              onClick={() => onReview?.(finding)}
              className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-black"
            >
              Review
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default function SupervisorDashboardView({ projectRole: projectRoleProp }) {
  const {
    activeProject,
    projectRole: contextProjectRole,
    currentUser,
  } = useProjectContext();
  const projectRole = projectRoleProp ?? contextProjectRole;
  const projectId = activeProject?.id;

  const [dashboard, setDashboard] = useState(null);
  const [findings, setFindings] = useState([]);
  const [escalationInbox, setEscalationInbox] = useState([]);
  const [queueView, setQueueView] = useState("own");
  const [team, setTeam] = useState([]);
  const [orgChart, setOrgChart] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [closeLoadingMap, setCloseLoadingMap] = useState({});
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [filters, setFilters] = useState({
    search: "",
    foreman: "",
    status: "",
    severity: "",
    openOnly: false,
    respondedOnly: false,
    overdueOnly: false,
    reviewOnly: false,
    workflow: "",
  });
  const [reassignFinding, setReassignFinding] = useState(null);
  const [reviewFinding, setReviewFinding] = useState(null);
  const [responseFinding, setResponseFinding] = useState(null);
  const [escalationFinding, setEscalationFinding] = useState(null);
  const [escalationTargets, setEscalationTargets] = useState([]);
  const [escalationTargetsLoading, setEscalationTargetsLoading] = useState(false);
  const [escalationSaving, setEscalationSaving] = useState(false);
  const [detailFinding, setDetailFinding] = useState(null);
  const [detailHistory, setDetailHistory] = useState([]);
  const [detailHistoryLoading, setDetailHistoryLoading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [findingsLoading, setFindingsLoading] = useState(false);
  const [visibilityAudit, setVisibilityAudit] = useState(null);

  const isProjectEngineerView = norm(projectRole) === "project_engineer";
  const canReviewIssue = ["general_foreman", "supervisor", "qa_qc_manager"].includes(norm(projectRole));
  const canAnswerForForeman = norm(projectRole) === "general_foreman";
  const canRespondAsResponsible = ["general_foreman", "supervisor"].includes(norm(projectRole));
  const canReassignIssue =
    isSystemAdminRole(currentUser?.role) ||
    ["general_foreman", "supervisor", "qa_qc_manager", "inspector", "project_manager"].includes(norm(projectRole));
  const canEscalateIssue = ["general_foreman", "project_engineer"].includes(norm(projectRole));
  const canUseSeparatedQueues = ["general_foreman", "project_engineer", "supervisor"].includes(norm(projectRole));
  const canUseEscalationInbox = ["general_foreman", "project_engineer", "supervisor"].includes(norm(projectRole));

  const loadAll = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setFindingsLoading(true);
    setFindings([]);
    setError("");

    // Supporting lookups can run beside the lightweight dashboard summary. The
    // paged issue-row scan starts only after the summary returns so the two largest
    // issue queries do not compete for the same 30-second request window.
    const teamPromise = projectsAPI.getTeam(projectId);
    const orgPromise = projectsAPI.getOrgChart(projectId);
    const escalationPromise = canUseEscalationInbox
      ? inspectionsAPI.listEscalationInbox(projectId)
      : Promise.resolve({ data: [] });

    try {
      const dashRes = await inspectionsAPI.getSupervisorDashboardSummary(projectId);
      setDashboard(dashRes.data || null);
    } catch (err) {
      setDashboard(null);
      setError(
        err?.response?.data?.detail ||
          err.message ||
          "Failed to load issue control metrics.",
      );
    } finally {
      // Only the metrics/matrix summary controls the main loading overlay.
      setLoading(false);
    }

    const findingsPromise = (async () => {
      const collected = [];
      let offset = 0;
      const limit = 100;
      try {
        while (true) {
          const res = await inspectionsAPI.listSupervisorFindingsPage(projectId, {
            offset,
            limit,
          });
          const payload = res?.data || {};
          const items = Array.isArray(payload.items) ? payload.items : [];
          collected.push(...items);
          setFindings([...collected]);
          if (!payload.has_more || items.length === 0) break;
          offset += items.length;
        }
        return collected;
      } finally {
        setFindingsLoading(false);
      }
    })();

    // Admin visibility is useful but not page-critical. Run it after the paged issue
    // scan finishes so Admin does not launch two complete issue-scope scans at once.
    const auditPromise = isSystemAdminRole(currentUser?.role)
      ? findingsPromise
          .catch(() => [])
          .then(() => inspectionsAPI.getIssueVisibilityAudit(projectId))
      : Promise.resolve({ data: null });

    const [teamResult, orgResult, escalationResult, findingsResult, auditResult] =
      await Promise.allSettled([
        teamPromise,
        orgPromise,
        escalationPromise,
        findingsPromise,
        auditPromise,
      ]);

    if (teamResult.status === "fulfilled") {
      setTeam(Array.isArray(teamResult.value.data) ? teamResult.value.data : []);
    } else {
      console.warn("Issue Control team data failed to load", teamResult.reason);
    }
    if (orgResult.status === "fulfilled") {
      setOrgChart(orgResult.value.data || null);
    } else {
      console.warn("Issue Control org chart failed to load", orgResult.reason);
    }
    if (escalationResult.status === "fulfilled") {
      setEscalationInbox(
        Array.isArray(escalationResult.value.data) ? escalationResult.value.data : [],
      );
    } else {
      console.warn("Issue Control escalation inbox failed to load", escalationResult.reason);
    }
    if (findingsResult.status === "rejected") {
      console.warn("Issue Control issue rows failed to load", findingsResult.reason);
      setError((current) =>
        current ||
        findingsResult.reason?.response?.data?.detail ||
        "Issue metrics loaded, but issue rows could not be loaded.",
      );
    }
    if (auditResult.status === "fulfilled") {
      setVisibilityAudit(auditResult.value?.data || null);
    } else if (isSystemAdminRole(currentUser?.role)) {
      console.warn("Issue visibility audit failed to load", auditResult.reason);
      setVisibilityAudit(null);
    }
  }, [projectId, canUseEscalationInbox, currentUser?.role]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const foremen = useMemo(() => {
    const role = norm(projectRole);
    let allowedUserIds = null;

    if (role === "general_foreman") {
      const actorMember = (team || []).find(
        (m) => Number(m.user_id) === Number(currentUser?.id),
      );
      const nodes = Array.isArray(orgChart?.nodes) ? orgChart.nodes : [];
      const children = new Map();
      nodes.forEach((node) => {
        if (node.reports_to_team_member_id == null) return;
        const parentId = Number(node.reports_to_team_member_id);
        const list = children.get(parentId) || [];
        list.push(Number(node.project_team_member_id));
        children.set(parentId, list);
      });
      const descendants = new Set();
      const stack = actorMember ? [...(children.get(Number(actorMember.id)) || [])] : [];
      while (stack.length) {
        const memberId = stack.pop();
        if (descendants.has(memberId)) continue;
        descendants.add(memberId);
        stack.push(...(children.get(memberId) || []));
      }
      allowedUserIds = new Set(
        nodes
          .filter(
            (node) =>
              descendants.has(Number(node.project_team_member_id)) &&
              norm(node.role) === "foreman",
          )
          .map((node) => Number(node.user_id)),
      );
    }

    const fromTeam = (team || [])
      .filter((m) => {
        if (m.is_active === false || !FIELD_RESPONSIBLE_ROLES.has(norm(m.role))) return false;
        if (allowedUserIds) {
          return norm(m.role) === "foreman" && allowedUserIds.has(Number(m.user_id));
        }
        return true;
      })
      .map((m) => ({
        user_id: Number(m.user_id),
        name:
          `${m.first_name || ""} ${m.last_name || ""}`.trim() ||
          m.email ||
          `User #${m.user_id}`,
        email: m.email,
      }));

    const ids = new Set(fromTeam.map((m) => m.user_id));
    if (!allowedUserIds) {
      (findings || []).forEach((f) => {
        if (f.responsible_user_id && !ids.has(Number(f.responsible_user_id))) {
          fromTeam.push({
            user_id: Number(f.responsible_user_id),
            name: f.responsible_user_name || `User #${f.responsible_user_id}`,
            email: f.responsible_user_email,
          });
          ids.add(Number(f.responsible_user_id));
        }
      });
    }
    return fromTeam.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [team, findings, orgChart, projectRole, currentUser?.id]);

  const laneForemanUserIds = useMemo(
    () => new Set((foremen || []).map((member) => Number(member.user_id))),
    [foremen],
  );

  const ownFindings = useMemo(
    () => (findings || []).filter((finding) => Number(finding.responsible_user_id) === Number(currentUser?.id)),
    [findings, currentUser?.id],
  );

  const teamFindings = useMemo(
    () => (findings || []).filter((finding) => Number(finding.responsible_user_id) !== Number(currentUser?.id)),
    [findings, currentUser?.id],
  );

  const visibleFindings = useMemo(() => {
    if (!canUseSeparatedQueues) return findings || [];
    if (queueView === "own") return ownFindings;
    if (queueView === "team") return teamFindings;
    return findings || [];
  }, [canUseSeparatedQueues, queueView, ownFindings, teamFindings, findings]);

  const queueMatrix = useMemo(() => {
    const roleByUserId = new Map((team || []).map((member) => [Number(member.user_id), norm(member.role)]));
    const roleLabels = ["foreman", "general_foreman", "project_engineer", "supervisor"];
    const rows = new Map(roleLabels.map((role) => [role, { role, own: 0, team: 0, escalated: 0, open: 0, overdue: 0 }]));

    (findings || []).forEach((finding) => {
      const userId = Number(finding.responsible_user_id);
      const role = roleByUserId.get(userId) || "other";
      if (!rows.has(role)) rows.set(role, { role, own: 0, team: 0, escalated: 0, open: 0, overdue: 0 });
      const row = rows.get(role);
      if (userId === Number(currentUser?.id)) row.own += 1;
      else row.team += 1;
      if (isOpenIssue(finding)) row.open += 1;
      if (isOpenIssue(finding) && isPastDate(finding.due_date)) row.overdue += 1;
    });

    (escalationInbox || []).forEach((item) => {
      const role = norm(item.to_role) || norm(projectRole) || "other";
      if (!rows.has(role)) rows.set(role, { role, own: 0, team: 0, escalated: 0, open: 0, overdue: 0 });
      rows.get(role).escalated += 1;
    });

    return Array.from(rows.values()).filter((row) => row.own || row.team || row.escalated || row.open || row.overdue);
  }, [team, findings, escalationInbox, currentUser?.id, projectRole]);

  const filteredFindings = useMemo(() => {
    return (visibleFindings || []).filter((f) => {
      const q = (filters.search || "").trim().toLowerCase();
      if (
        q &&
        ![
          f.finding_number,
          f.description,
          f.corrective_action,
          f.location,
          f.status,
          f.severity,
          f.responsible_user_name,
          f.inspection_type,
          f.inspection_id,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(q)
      )
        return false;
      if (
        filters.foreman &&
        Number(f.responsible_user_id) !== Number(filters.foreman)
      )
        return false;
      if (filters.status && norm(f.status) !== norm(filters.status))
        return false;
      if (filters.severity && norm(f.severity) !== norm(filters.severity))
        return false;
      if (filters.workflow === "active" && isClosedFindingStatus(f?.status))
        return false;
      if (filters.workflow === "closed" && !isClosedFindingStatus(f?.status))
        return false;
      if (filters.openOnly && !isOpenIssue(f)) return false;
      if (filters.respondedOnly && !hasCompletedAssigneeResponse(f)) return false;
      if (filters.overdueOnly && (!isOpenIssue(f) || !isPastDate(f.due_date)))
        return false;
      if (filters.reviewOnly && !needsReview(f)) return false;
      return true;
    });
  }, [visibleFindings, filters]);

  const statuses = useMemo(
    () =>
      Array.from(
        new Set((visibleFindings || []).map((f) => norm(f.status)).filter(Boolean)),
      ).sort(),
    [visibleFindings],
  );
  const severities = useMemo(
    () =>
      Array.from(
        new Set((visibleFindings || []).map((f) => norm(f.severity)).filter(Boolean)),
      ).sort(),
    [visibleFindings],
  );

  const counts = dashboard?.counts || {};
  const wholeScopeView = !canUseSeparatedQueues || queueView === "all";
  const exactOpenCount = useMemo(
    () => wholeScopeView ? Number(counts.open || 0) : (visibleFindings || []).filter(isOpenIssue).length,
    [wholeScopeView, counts.open, visibleFindings],
  );
  const visibleOverdueCount = useMemo(
    () => wholeScopeView
      ? Number(counts.overdue || 0)
      : (visibleFindings || []).filter(
          (finding) => isOpenIssue(finding) && isPastDate(finding?.due_date),
        ).length,
    [wholeScopeView, counts.overdue, visibleFindings],
  );
  const visibleNeedReviewCount = useMemo(
    () => wholeScopeView ? Number(counts.need_review ?? counts.waiting_review ?? 0) : (visibleFindings || []).filter(needsReview).length,
    [wholeScopeView, counts.need_review, counts.waiting_review, visibleFindings],
  );
  const visibleTotalCount = wholeScopeView
    ? Number(counts.total ?? counts.total_foreman_findings ?? 0)
    : visibleFindings.length;
  const visibleActiveCount = wholeScopeView
    ? Number(counts.active || 0)
    : (visibleFindings || []).filter((finding) => !isClosedFindingStatus(finding?.status)).length;
  const visibleClosedCount = wholeScopeView
    ? Number(counts.closed || 0)
    : (visibleFindings || []).filter((finding) => isClosedFindingStatus(finding?.status)).length;

  const openForemanWorkload = useMemo(() => {
    const rowsByUser = new Map();

    (dashboard?.by_foreman || []).forEach((sourceRow) => {
      const userId = Number(sourceRow?.user_id);
      if (!userId) return;
      rowsByUser.set(userId, {
        user_id: userId,
        name: sourceRow?.name,
        status_counts: {},
        active_total: 0,
        overdue: 0,
        waiting_review: 0,
      });
    });

    (visibleFindings || []).forEach((finding) => {
      const userId = Number(finding?.responsible_user_id);
      if (!userId || isClosedFindingStatus(finding?.status)) return;

      const row = rowsByUser.get(userId) || {
        user_id: userId,
        name:
          finding?.responsible_user_name ||
          `User #${finding?.responsible_user_id}`,
        status_counts: {},
        active_total: 0,
        overdue: 0,
        waiting_review: 0,
      };

      const status = norm(finding?.status) || "not_set";
      row.status_counts[status] = Number(row.status_counts[status] || 0) + 1;
      row.active_total += 1;

      if (isOpenIssue(finding) && isPastDate(finding?.due_date)) row.overdue += 1;

      if (needsReview(finding)) {
        row.waiting_review += 1;
      }

      rowsByUser.set(userId, row);
    });

    return Array.from(rowsByUser.values())
      .map((row) => ({
        ...row,
        open: (visibleFindings || []).filter(
          (finding) =>
            Number(finding?.responsible_user_id) === Number(row.user_id) &&
            isOpenIssue(finding),
        ).length,
        responded: (visibleFindings || []).filter(
          (finding) =>
            Number(finding?.responsible_user_id) === Number(row.user_id) &&
            !isClosedFindingStatus(finding?.status) &&
            hasCompletedAssigneeResponse(finding),
        ).length,
        pending: Number(row.status_counts?.pending || 0),
      }))
      .filter((row) => Number(row.active_total || 0) > 0)
      .sort(
        (a, b) =>
          Number(b.open || 0) - Number(a.open || 0) ||
          Number(b.overdue || 0) - Number(a.overdue || 0) ||
          Number(b.active_total || 0) - Number(a.active_total || 0) ||
          (a.name || "").localeCompare(b.name || ""),
      );
  }, [dashboard?.by_foreman, visibleFindings]);

  const applyWorkloadFilter = useCallback((userId, patch = {}) => {
    setFilters({
      search: "",
      foreman: String(userId),
      status: "",
      severity: "",
      openOnly: false,
      respondedOnly: false,
      overdueOnly: false,
      reviewOnly: false,
      workflow: "",
      ...patch,
    });
  }, []);

  const applySummaryFilter = useCallback((patch = {}) => {
    setFilters({
      search: "",
      foreman: "",
      status: "",
      severity: "",
      openOnly: false,
      respondedOnly: false,
      overdueOnly: false,
      reviewOnly: false,
      workflow: "",
      ...patch,
    });
  }, []);

  const openFindingContext = useCallback(
    async (finding) => {
      setDetailFinding(finding);
      setDetailHistory([]);
      if (!projectId || !finding?.inspection_id || !finding?.id) return;
      setDetailHistoryLoading(true);
      try {
        const [contextResult, historyResult] = await Promise.allSettled([
          inspectionsAPI.getSupervisorFindingContext(projectId, finding.id),
          inspectionsAPI.listFindingComments(
            projectId,
            finding.inspection_id,
            finding.id,
          ),
        ]);
        if (contextResult.status === "fulfilled" && contextResult.value?.data) {
          setDetailFinding(contextResult.value.data);
        }
        if (historyResult.status === "fulfilled") {
          setDetailHistory(
            Array.isArray(historyResult.value.data) ? historyResult.value.data : [],
          );
        } else {
          throw historyResult.reason;
        }
      } catch (err) {
        setError(
          err?.response?.data?.detail || "Failed to load issue context/history.",
        );
      } finally {
        setDetailHistoryLoading(false);
      }
    },
    [projectId],
  );

  const openRelatedFile = useCallback(
    async (_finding, file) => {
      if (!projectId || !file?.id) return;
      const previewWindow = window.open("", "_blank");
      if (previewWindow) {
        previewWindow.opener = null;
        previewWindow.document.title = file.name || "Opening file";
        previewWindow.document.body.innerHTML =
          '<div style="font-family:Arial,sans-serif;padding:24px;color:#374151">Opening file…</div>';
      }
      try {
        const res = await inspectionsAPI.getDocumentDownloadUrl(projectId, file.id);
        const freshUrl = res?.data?.url;
        if (!freshUrl) throw new Error("A fresh file link could not be generated.");
        if (previewWindow && !previewWindow.closed) previewWindow.location.replace(freshUrl);
        else window.location.assign(freshUrl);
      } catch (err) {
        if (previewWindow && !previewWindow.closed) previewWindow.close();
        setError(err?.response?.data?.detail || err?.message || "Failed to open file.");
      }
    },
    [projectId],
  );

  const copyIssueNumber = async (finding) => {
    await navigator.clipboard.writeText(
      finding.finding_number || `Issue #${finding.id}`,
    );
    setCopiedId(finding.id);
    window.setTimeout(() => setCopiedId(null), 1400);
  };

  const doReassign = async (payload) => {
    if (!projectId || !reassignFinding?.id || saving) return;
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      await supervisorAPI.reassignFinding(
        projectId,
        reassignFinding.id,
        payload,
      );
      setSuccess("Issue reassigned and responsible user notified.");
      setReassignFinding(null);
      await loadAll();
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err.message ||
          "Failed to reassign issue.",
      );
    } finally {
      setSaving(false);
    }
  };

  const openEscalation = async (finding) => {
    if (!projectId || !finding?.id) return;
    setEscalationFinding(finding);
    setEscalationTargets([]);
    setEscalationTargetsLoading(true);
    setError("");
    try {
      const res = await inspectionsAPI.getFindingEscalationTargets(projectId, finding.id);
      setEscalationTargets(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to load escalation targets.");
    } finally {
      setEscalationTargetsLoading(false);
    }
  };

  const doEscalate = async (payload) => {
    if (!projectId || !escalationFinding?.id || escalationSaving) return;
    setEscalationSaving(true);
    setError("");
    setSuccess("");
    try {
      await inspectionsAPI.createFindingEscalation(projectId, escalationFinding.id, payload);
      setSuccess("Issue escalated through the published reporting path.");
      setEscalationFinding(null);
      setEscalationTargets([]);
      await loadAll();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Failed to escalate issue.");
    } finally {
      setEscalationSaving(false);
    }
  };

  const doResponsibleResponse = async (payload) => {
    if (!projectId || !responseFinding?.inspection_id || !responseFinding?.id || saving) return;
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      await inspectionsAPI.submitFindingResponse(
        projectId,
        responseFinding.inspection_id,
        responseFinding.id,
        payload,
      );
      setSuccess(norm(projectRole) === "supervisor" ? "Supervisor resolution action saved and sent for review." : "Lane Foreman response saved by the General Foreman.");
      setResponseFinding(null);
      await loadAll();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Failed to submit the lane response.");
    } finally {
      setSaving(false);
    }
  };

  const doReview = async (payload) => {
    if (!projectId || !reviewFinding?.id || saving) return;
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      await inspectionsAPI.reviewFinding(projectId, reviewFinding.id, payload);
      setSuccess("Review saved. The issue was closed automatically when the review resolved it.");
      setReviewFinding(null);
      await loadAll();
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err.message ||
          "Failed to save review.",
      );
    } finally {
      setSaving(false);
    }
  };


  if (!activeProject) return null;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-xl font-bold text-gray-900">
            <HardHat className="h-5 w-5 text-orange-500" />
            Issues Control
          </h2>
          <p className="text-sm text-gray-500">
            Organization-aware field issue control. Visibility and actions are
            scoped by project role and reporting line.
          </p>
        </div>
        <button
          type="button"
          onClick={loadAll}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-60"
        >
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>
      </div>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}
      {success ? (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">
          {success}
        </div>
      ) : null}

      {canUseSeparatedQueues ? (
        <div className="flex flex-wrap gap-1 rounded-xl border border-gray-200 bg-gray-50 p-1">
          <button
            type="button"
            onClick={() => setQueueView("own")}
            className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${queueView === "own" ? "bg-white text-gray-900 shadow-sm" : "text-gray-600 hover:text-gray-900"}`}
          >
            Own Queue ({ownFindings.length})
          </button>
          <button
            type="button"
            onClick={() => setQueueView("team")}
            className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${queueView === "team" ? "bg-white text-gray-900 shadow-sm" : "text-gray-600 hover:text-gray-900"}`}
          >
            Team Queue ({teamFindings.length})
          </button>
          {canUseEscalationInbox ? (
            <button
              type="button"
              onClick={() => setQueueView("escalated")}
              className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${queueView === "escalated" ? "bg-orange-500 text-white shadow-sm" : "text-gray-600 hover:text-gray-900"}`}
            >
              Escalated to Me (
              {
                escalationInbox.filter((item) => norm(item.status) === "open")
                  .length
              }
              )
            </button>
          ) : null}
        </div>
      ) : null}

      {canUseSeparatedQueues && queueMatrix.length ? (
        <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="border-b px-4 py-3">
            <h3 className="font-semibold text-gray-900">Issue Queue Matrix</h3>
            <p className="text-xs text-gray-500">
              Current reporting-scope workload split by role and queue source.
            </p>
          </div>
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-2">Role</th>
                <th className="px-4 py-2">Own</th>
                <th className="px-4 py-2">Team</th>
                <th className="px-4 py-2">Escalated</th>
                <th className="px-4 py-2">Open</th>
                <th className="px-4 py-2">Overdue</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {queueMatrix.map((row) => (
                <tr key={row.role}>
                  <td className="px-4 py-2 font-semibold text-gray-800">
                    {titleCase(row.role)}
                  </td>
                  <td className="px-4 py-2">{row.own}</td>
                  <td className="px-4 py-2">{row.team}</td>
                  <td className="px-4 py-2">{row.escalated}</td>
                  <td className="px-4 py-2">{row.open}</td>
                  <td className="px-4 py-2 text-red-700">{row.overdue}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {loading ? (
        <div className="flex items-center justify-center rounded-2xl border bg-white p-10">
          <LoadingSpinner />
        </div>
      ) : canUseEscalationInbox && queueView === "escalated" ? (
        <EscalationInboxPanel
          projectId={projectId}
          projectRole={projectRole}
          items={escalationInbox}
          onRefresh={loadAll}
          onError={setError}
          onSuccess={setSuccess}
        />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-6">
            <KpiCard
              label="Total Issues"
              value={visibleTotalCount}
              hint="All issues in your reporting scope"
              icon={ClipboardList}
              onClick={() => applySummaryFilter({})}
              active={
                !filters.workflow &&
                !filters.openOnly &&
                !filters.reviewOnly &&
                !filters.overdueOnly &&
                !filters.respondedOnly &&
                !filters.foreman &&
                !filters.status &&
                !filters.severity &&
                !filters.search
              }
            />
            <KpiCard
              label="Active"
              value={visibleActiveCount}
              hint="Everything not closed"
              icon={HardHat}
              onClick={() => applySummaryFilter({ workflow: "active" })}
              active={
                filters.workflow === "active" &&
                !filters.openOnly &&
                !filters.reviewOnly &&
                !filters.overdueOnly &&
                !filters.respondedOnly &&
                !filters.foreman &&
                !filters.status &&
                !filters.severity
              }
            />
            <KpiCard
              label="Open"
              value={exactOpenCount}
              hint="Awaiting final field response"
              icon={Clock}
              onClick={() => applySummaryFilter({ openOnly: true })}
              active={
                filters.openOnly &&
                !filters.workflow &&
                !filters.foreman &&
                !filters.status &&
                !filters.severity &&
                !filters.respondedOnly &&
                !filters.overdueOnly &&
                !filters.reviewOnly
              }
            />
            <KpiCard
              label="Need Review"
              value={visibleNeedReviewCount}
              hint="Final response awaiting authorized review"
              icon={BadgeCheck}
              onClick={() => applySummaryFilter({ reviewOnly: true })}
              active={
                filters.reviewOnly &&
                !filters.workflow &&
                !filters.foreman &&
                !filters.status &&
                !filters.severity &&
                !filters.openOnly &&
                !filters.respondedOnly &&
                !filters.overdueOnly
              }
            />
            <KpiCard
              label="Closed"
              value={visibleClosedCount}
              hint="Authorized resolution/review completed"
              icon={Check}
              onClick={() => applySummaryFilter({ workflow: "closed" })}
              active={
                filters.workflow === "closed" &&
                !filters.openOnly &&
                !filters.reviewOnly &&
                !filters.overdueOnly &&
                !filters.respondedOnly &&
                !filters.foreman &&
                !filters.status &&
                !filters.severity
              }
            />
            <KpiCard
              label="Overdue"
              value={visibleOverdueCount}
              hint="Open issue past due"
              icon={AlertTriangle}
              onClick={() => applySummaryFilter({ overdueOnly: true })}
              active={
                filters.overdueOnly &&
                !filters.workflow &&
                !filters.foreman &&
                !filters.status &&
                !filters.severity &&
                !filters.openOnly &&
                !filters.respondedOnly &&
                !filters.reviewOnly
              }
            />
          </div>

          <div className="rounded-2xl border border-slate-200 bg-gradient-to-r from-slate-50 via-white to-orange-50/60 px-3 py-2 shadow-sm">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-white">
                Metric legend
              </span>
              {ISSUE_METRIC_LEGEND.map((item) => (
                <span
                  key={item.label}
                  title={`${item.label}: ${item.description}`}
                  className="inline-flex min-w-0 items-center gap-1.5 rounded-full border border-white bg-white px-2.5 py-1 text-[11px] text-gray-600 shadow-sm ring-1 ring-slate-100"
                >
                  <span
                    className={`h-2 w-2 shrink-0 rounded-full ${item.dot}`}
                  />
                  <strong className="whitespace-nowrap text-gray-900">
                    {item.label}
                  </strong>
                  <span className="hidden whitespace-nowrap text-gray-500 2xl:inline">
                    · {item.description}
                  </span>
                </span>
              ))}
            </div>
          </div>

          {isSystemAdminRole(currentUser?.role) && visibilityAudit ? (
            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
              <div className="border-b px-4 py-3">
                <h3 className="font-semibold text-gray-900">
                  Issue Visibility Audit
                </h3>
                <p className="text-xs text-gray-500">
                  Counts show exactly what each Operations Manager or Supervisor
                  can see from the current Org Chart. All Operations Managers
                  share the complete lane scope.
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-4 py-2">User</th>
                      <th className="px-4 py-2">Role</th>
                      <th className="px-4 py-2">Lanes</th>
                      <th className="px-4 py-2">Total</th>
                      <th className="px-4 py-2">Active</th>
                      <th className="px-4 py-2">Open</th>
                      <th className="px-4 py-2">Need Review</th>
                      <th className="px-4 py-2">Closed</th>
                      <th className="px-4 py-2">Overdue</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {(visibilityAudit.rows || []).map((row) => (
                      <tr key={`${row.role}-${row.user_id}`}>
                        <td className="px-4 py-2 font-medium text-gray-900">
                          {row.name || row.email || `User ${row.user_id}`}
                        </td>
                        <td className="px-4 py-2">{titleCase(row.role)}</td>
                        <td className="max-w-[260px] px-4 py-2 text-xs text-gray-600">
                          {(row.lanes || []).length
                            ? row.lanes.join(", ")
                            : "—"}
                        </td>
                        <td className="px-4 py-2">{row.total || 0}</td>
                        <td className="px-4 py-2">{row.active || 0}</td>
                        <td className="px-4 py-2">{row.open || 0}</td>
                        <td className="px-4 py-2">{row.need_review || 0}</td>
                        <td className="px-4 py-2">{row.closed || 0}</td>
                        <td className="px-4 py-2 text-red-700">
                          {row.overdue || 0}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          {findingsLoading ? (
            <div className="rounded-xl border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-700">
              Metrics are ready. Loading remaining issue rows in small batches…
            </div>
          ) : null}

          <div className="grid min-w-0 grid-cols-1 gap-4 xl:grid-cols-[360px_minmax(0,1fr)]">
            <div className="min-w-0 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="mb-3 flex items-center gap-2">
                <UserRoundCog className="h-4 w-4 text-orange-500" />
                <h3 className="font-semibold text-gray-900">Field workload</h3>
              </div>
              <div className="space-y-2">
                {openForemanWorkload.length === 0 ? (
                  <div className="rounded-lg border border-dashed p-4 text-sm text-gray-500">
                    No active field issues.
                  </div>
                ) : (
                  openForemanWorkload.map((row) => {
                    const openActive =
                      filters.foreman === String(row.user_id) &&
                      filters.openOnly &&
                      !filters.status &&
                      !filters.reviewOnly &&
                      !filters.overdueOnly;
                    const respondedActive =
                      filters.foreman === String(row.user_id) &&
                      filters.respondedOnly &&
                      !filters.status &&
                      !filters.openOnly &&
                      !filters.reviewOnly &&
                      !filters.overdueOnly;
                    const pendingActive =
                      filters.foreman === String(row.user_id) &&
                      filters.status === "pending" &&
                      !filters.reviewOnly &&
                      !filters.overdueOnly;
                    const overdueActive =
                      filters.foreman === String(row.user_id) &&
                      filters.overdueOnly;
                    const reviewActive =
                      filters.foreman === String(row.user_id) &&
                      filters.reviewOnly &&
                      !filters.status &&
                      !filters.severity &&
                      !filters.overdueOnly;

                    const filterButtonClass = (active, tone) => {
                      const tones = {
                        blue: active
                          ? "border-blue-500 bg-blue-500 text-white"
                          : "border-blue-100 bg-blue-50 text-blue-700 hover:border-blue-300 hover:bg-blue-100",
                        amber: active
                          ? "border-amber-500 bg-amber-500 text-white"
                          : "border-amber-100 bg-amber-50 text-amber-700 hover:border-amber-300 hover:bg-amber-100",
                        orange: active
                          ? "border-orange-500 bg-orange-500 text-white"
                          : "border-orange-100 bg-orange-50 text-orange-700 hover:border-orange-300 hover:bg-orange-100",
                        emerald: active
                          ? "border-emerald-600 bg-emerald-600 text-white"
                          : "border-emerald-100 bg-emerald-50 text-emerald-700 hover:border-emerald-300 hover:bg-emerald-100",
                        red: active
                          ? "border-red-500 bg-red-500 text-white"
                          : "border-red-100 bg-red-50 text-red-700 hover:border-red-300 hover:bg-red-100",
                      };
                      return `flex min-w-0 items-center justify-between gap-2 rounded-lg border px-2.5 py-2 text-xs font-semibold transition ${tones[tone]}`;
                    };

                    return (
                      <div
                        key={row.user_id}
                        className="rounded-xl border border-gray-100 bg-gray-50 p-3"
                      >
                        <div className="flex min-w-0 items-center justify-between gap-2">
                          <div className="min-w-0 truncate text-sm font-semibold text-gray-900">
                            {row.name || `User #${row.user_id}`}
                          </div>
                          <button
                            type="button"
                            onClick={() =>
                              applyWorkloadFilter(row.user_id, {
                                openOnly: true,
                              })
                            }
                            className={`inline-flex shrink-0 items-center gap-2 rounded-lg border px-2.5 py-1.5 text-xs font-semibold transition ${
                              openActive
                                ? "border-emerald-600 bg-emerald-600 text-white shadow-sm"
                                : "border-emerald-200 bg-white text-emerald-700 hover:border-emerald-400 hover:bg-emerald-50"
                            }`}
                            title="Show this person’s Open/Pending issues awaiting an assignee response"
                          >
                            <span>Open issues</span>
                            <span className="text-base font-black leading-none">
                              {row.open || 0}
                            </span>
                          </button>
                        </div>

                        <div className="mt-3 grid grid-cols-2 gap-1.5">
                          <button
                            type="button"
                            onClick={() =>
                              applyWorkloadFilter(row.user_id, {
                                respondedOnly: true,
                              })
                            }
                            className={filterButtonClass(
                              respondedActive,
                              "blue",
                            )}
                          >
                            <span>Responded</span>
                            <span>{row.responded || 0}</span>
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              applyWorkloadFilter(row.user_id, {
                                status: "pending",
                              })
                            }
                            className={filterButtonClass(
                              pendingActive,
                              "amber",
                            )}
                          >
                            <span>Pending</span>
                            <span>{row.pending || 0}</span>
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              applyWorkloadFilter(row.user_id, {
                                overdueOnly: true,
                              })
                            }
                            className={filterButtonClass(overdueActive, "red")}
                          >
                            <span>Overdue</span>
                            <span>{row.overdue || 0}</span>
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              applyWorkloadFilter(row.user_id, {
                                reviewOnly: true,
                              })
                            }
                            className={`${filterButtonClass(
                              reviewActive,
                              "emerald",
                            )} col-span-2`}
                          >
                            <span>Need review</span>
                            <span>{row.waiting_review || 0}</span>
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="font-semibold text-gray-900">Field issues</h3>
                  <p className="text-xs text-gray-500">
                    Filter, reassign, and review submitted field work.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    setFilters({
                      search: "",
                      foreman: "",
                      status: "",
                      severity: "",
                      openOnly: false,
                      respondedOnly: false,
                      overdueOnly: false,
                      reviewOnly: false,
                      workflow: "",
                    })
                  }
                  className="rounded-lg border px-3 py-1.5 text-xs hover:bg-gray-50"
                >
                  Clear filters
                </button>
              </div>

              <div className="mb-4 grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-5">
                <label className="flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 xl:col-span-2">
                  <Search className="h-4 w-4 text-gray-400" />
                  <input
                    value={filters.search}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        search: e.target.value,
                      }))
                    }
                    placeholder="Search issue number, description, inspection, responsible person…"
                    className="w-full text-sm outline-none"
                  />
                </label>
                <select
                  value={filters.foreman}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, foreman: e.target.value }))
                  }
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="">All responsible users</option>
                  {foremen.map((m) => (
                    <option key={m.user_id} value={m.user_id}>
                      {m.name}
                    </option>
                  ))}
                </select>
                <select
                  value={filters.status}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      status: e.target.value,
                      openOnly: false,
                      respondedOnly: false,
                      workflow: "",
                    }))
                  }
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="">All statuses</option>
                  {statuses.map((s) => (
                    <option key={s} value={s}>
                      Status: {titleCase(s)}
                    </option>
                  ))}
                </select>
                <select
                  value={filters.severity}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      severity: e.target.value,
                    }))
                  }
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="">All severities</option>
                  {severities.map((s) => (
                    <option key={s} value={s}>
                      {titleCase(s)}
                    </option>
                  ))}
                </select>
                <label className="flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm">
                  <input
                    type="checkbox"
                    checked={filters.overdueOnly}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        overdueOnly: e.target.checked,
                        openOnly: e.target.checked ? false : prev.openOnly,
                        respondedOnly: e.target.checked
                          ? false
                          : prev.respondedOnly,
                        workflow: e.target.checked ? "" : prev.workflow,
                      }))
                    }
                    className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                  />
                  Overdue only
                </label>
              </div>

              <div className="w-full overflow-hidden rounded-xl border">
                <table className="w-full table-fixed text-sm">
                  <colgroup>
                    <col className="w-[34%]" />
                    <col className="w-[14%]" />
                    <col className="w-[10%]" />
                    <col className="w-[14%]" />
                    <col className="w-[14%]" />
                    <col className="w-[14%]" />
                  </colgroup>
                  <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-3 py-2">Issues</th>
                      <th className="px-3 py-2">Responsible</th>
                      <th className="px-3 py-2">Due</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2">Review</th>
                      <th className="px-3 py-2 text-right">
                        {isProjectEngineerView ? "View" : "Actions"}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredFindings.length === 0 ? (
                      <tr>
                        <td
                          colSpan={6}
                          className="px-3 py-8 text-center text-gray-500"
                        >
                          No matching issues.
                        </td>
                      </tr>
                    ) : (
                      filteredFindings.map((f) => (
                        <tr
                          key={f.id}
                          className="group align-top hover:bg-gray-50"
                        >
                          <td className="max-w-sm px-3 py-3">
                            <div className="flex items-center gap-2 font-semibold text-gray-900">
                              <span>
                                {f.finding_number || `Issue #${f.id}`}
                              </span>
                              <button
                                type="button"
                                onClick={() => copyIssueNumber(f)}
                                title="Copy issue number"
                                className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-orange-600"
                              >
                                {copiedId === f.id ? (
                                  <Check className="h-3.5 w-3.5" />
                                ) : (
                                  <Copy className="h-3.5 w-3.5" />
                                )}
                              </button>
                            </div>
                            <div className="mt-1 line-clamp-2 text-xs text-gray-600">
                              {f.description}
                            </div>
                          </td>
                          <td className="px-3 py-3 text-xs text-gray-600">
                            <div
                              className="truncate font-medium text-gray-900"
                              title={f.responsible_user_name || "-"}
                            >
                              {f.responsible_user_name || "-"}
                            </div>
                          </td>
                          <td className="px-3 py-3 text-xs">
                            <span
                              className={
                                isPastDate(f.due_date) &&
                                norm(f.status) !== "closed"
                                  ? "font-semibold text-red-600"
                                  : "text-gray-700"
                              }
                            >
                              {f.due_date || "-"}
                            </span>
                          </td>
                          <td className="px-3 py-3">
                            <span
                              className={`rounded-full border px-2 py-0.5 text-xs ${statusBadge(f.status)}`}
                            >
                              {titleCase(f.status)}
                            </span>
                            {f.foreman_action ? (
                              <div className="mt-1 text-xs text-gray-500">
                                {f.foreman_action}
                              </div>
                            ) : null}
                          </td>
                          <td className="px-3 py-3">
                            <span
                              className={`rounded-full border px-2 py-0.5 text-xs ${reviewBadge(f.review_status || f.gf_review_status)}`}
                            >
                              {needsReview(f)
                                ? "Need Review"
                                : reviewDecisionLabel(
                                    f.review_status || f.gf_review_status,
                                  )}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-right">
                            <div className="flex flex-col items-stretch justify-end gap-1.5">
                              <button
                                type="button"
                                onClick={() => openFindingContext(f)}
                                className="inline-flex items-center justify-center gap-1 rounded-lg border border-gray-300 px-2 py-1.5 text-xs hover:bg-gray-50"
                                title="Open issue context and history"
                              >
                                <Eye className="h-3.5 w-3.5" /> Context
                              </button>
                              {canReassignIssue ||
                              canEscalateIssue ||
                              canReviewIssue ||
                              canAnswerForForeman ||
                              (canRespondAsResponsible &&
                                Number(f.responsible_user_id) ===
                                  Number(currentUser?.id)) ? (
                                <select
                                  defaultValue=""
                                  onChange={(e) => {
                                    const action = e.target.value;
                                    e.target.value = "";
                                    if (action === "reassign")
                                      setReassignFinding(f);
                                    if (action === "escalate")
                                      openEscalation(f);
                                    if (action === "review")
                                      setReviewFinding(f);
                                    if (
                                      action === "answer-for-foreman" ||
                                      action === "responsible-response"
                                    )
                                      setResponseFinding(f);
                                  }}
                                  disabled={!!closeLoadingMap[f.id]}
                                  className="w-full min-w-0 rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-xs font-medium text-gray-700"
                                  aria-label={`Actions for ${f.finding_number || `Issue ${f.id}`}`}
                                >
                                  <option value="">Actions…</option>
                                  {canReassignIssue ? (
                                    <option value="reassign">Reassign</option>
                                  ) : null}
                                  {canEscalateIssue &&
                                  Number(f.responsible_user_id) ===
                                    Number(currentUser?.id) &&
                                  norm(f.status) !== "closed" ? (
                                    <option value="escalate">Escalate</option>
                                  ) : null}
                                  {canReviewIssue &&
                                  needsReview(f) &&
                                  (norm(projectRole) !== "general_foreman" ||
                                    laneForemanUserIds.has(
                                      Number(f.responsible_user_id),
                                    )) ? (
                                    <option value="review">Review</option>
                                  ) : null}
                                  {canAnswerForForeman &&
                                  norm(f.responsible_user_role) === "foreman" &&
                                  laneForemanUserIds.has(
                                    Number(f.responsible_user_id),
                                  ) &&
                                  norm(f.status) !== "closed" ? (
                                    <option value="answer-for-foreman">
                                      Answer for Foreman
                                    </option>
                                  ) : null}
                                  {canRespondAsResponsible &&
                                  Number(f.responsible_user_id) ===
                                    Number(currentUser?.id) &&
                                  norm(f.status) !== "closed" ? (
                                    <option value="responsible-response">
                                      Add Resolution Action
                                    </option>
                                  ) : null}
                                </select>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}

      <FindingScenarioModal
        open={!!detailFinding}
        finding={detailFinding}
        history={detailHistory}
        historyLoading={detailHistoryLoading}
        onClose={() => setDetailFinding(null)}
        onReassign={(finding) => {
          setDetailFinding(null);
          setReassignFinding(finding);
        }}
        onReview={(finding) => {
          setDetailFinding(null);
          setReviewFinding(finding);
        }}
        onOpenFile={openRelatedFile}
        canReview={
          canReviewIssue &&
          needsReview(detailFinding) &&
          (norm(projectRole) !== "general_foreman" ||
            laneForemanUserIds.has(Number(detailFinding?.responsible_user_id)))
        }
        canReassign={canReassignIssue}
      />
      <ReassignModal
        open={!!reassignFinding}
        finding={reassignFinding}
        foremen={foremen}
        loading={saving}
        onClose={() => setReassignFinding(null)}
        onSubmit={doReassign}
      />
      <EscalateFindingModal
        open={!!escalationFinding}
        finding={escalationFinding}
        targets={escalationTargets}
        targetsLoading={escalationTargetsLoading}
        loading={escalationSaving}
        onClose={() => setEscalationFinding(null)}
        onSubmit={doEscalate}
      />
      <ResponsibleResponseModal
        open={!!responseFinding}
        finding={responseFinding}
        actorRole={projectRole}
        loading={saving}
        onClose={() => setResponseFinding(null)}
        onSubmit={doResponsibleResponse}
      />
      <ReviewModal
        open={!!reviewFinding}
        finding={reviewFinding}
        loading={saving}
        onClose={() => setReviewFinding(null)}
        onSubmit={doReview}
      />
    </div>
  );
}
